import React, { FC, useMemo } from 'react';

import { Select, chakraComponents } from 'chakra-react-select';
import { Box, Flex, FormControl, FormLabel, Text } from '@chakra-ui/react';

import { useSKU } from 'hooks/useSKU';
import { useFormikContext } from 'formik';

import { SKU } from 'types/sku';

import { PackageSelectProps } from './PackageSelect.props';

/**
 * A package's manufacturer and colour live on the item SKUs it contains, not on
 * the package itself — the list endpoint joins them in under items[].details.
 * Packages are normally single-material, so the first item that carries a value
 * describes the package; distinct extras are summarised rather than listed.
 */
export const summarise = (sku: SKU, field: 'manufacturer' | 'primaryColor') => {
  const values = (sku?.items || [])
    .map((item) => {
      const details = item?.details as any;

      // manufacturer is a manufacturers_mst ref, so the joined name is what
      // reads as a manufacturer; primaryColor is stored on the SKU itself.
      return field === 'manufacturer'
        ? details?.manufacturerDetails?.name
        : details?.[field];
    })
    .filter((value: string) => Boolean(value));

  const unique = Array.from(new Set<string>(values));

  if (unique.length === 0) {
    return '';
  }

  return unique.length > 1 ? `${unique[0]} +${unique.length - 1}` : unique[0];
};

type Option = {
  label: string;
  value: string;
  manufacturer: string;
  primaryColor: string;
};

/** Renders the name with its manufacturer/colour beneath, for menu and value. */
const OptionDetail: FC<{ option: Option }> = ({ option }) => {
  const meta = [option.manufacturer, option.primaryColor].filter(Boolean);

  return (
    <Box>
      <Text fontSize="14px">{option.label}</Text>
      {meta.length > 0 && (
        <Flex gap="6px" mt="2px" align="center">
          <Text fontSize="12px" color="support.400" noOfLines={1}>
            {meta.join(' · ')}
          </Text>
        </Flex>
      )}
    </Box>
  );
};

const customComponents = {
  Option: ({ children, ...props }: any) => (
    <chakraComponents.Option {...props}>
      <OptionDetail option={props.data} />
    </chakraComponents.Option>
  ),
};

const PackageSelectView: FC<PackageSelectProps> = ({
  values,
  washHubId,
  isReadOnly,
}) => {
  const { data, isLoading } = useSKU('package', washHubId);
  const { setFieldValue } = useFormikContext<any>();

  const toOption = (p: SKU): Option => ({
    label: p.name,
    value: p._id,
    manufacturer: summarise(p, 'manufacturer'),
    primaryColor: summarise(p, 'primaryColor'),
  });

  const options = useMemo(() => data?.map(toOption), [data]);

  const _values = useMemo(
    () =>
      values
        ? data?.filter((p) => values?.includes(p._id))?.map(toOption)
        : undefined,
    [data, values],
  );

  return (
    <FormControl mt={3} isRequired>
      <FormLabel
        htmlFor="packages"
        textTransform="capitalize"
        fontSize="14px"
        color="#afafaf">
        Assigned Package SKU
      </FormLabel>
      <Select
        isMulti
        value={_values}
        isDisabled={isLoading || isReadOnly}
        isLoading={isLoading}
        options={options}
        tagVariant="solid"
        colorScheme="primary"
        closeMenuOnSelect={false}
        components={customComponents}
        chakraStyles={{
          dropdownIndicator: (_props) => ({
            ..._props,
            bg: 'transparent',
          }),
          option: (_props) => ({
            ..._props,
            padding: '10px',
            borderBottom: '1px solid #ccc',
          }),
          menuList: (_props) => ({
            ..._props,
            padding: '0px',
            // Capped so a long package list scrolls inside the menu instead of
            // running past the bottom of the modal.
            maxHeight: '260px',
            overflowY: 'auto',
          }),
        }}
        onChange={(newValue) => {
          const packages = newValue.map((n) => n.value);

          setFieldValue('packages', packages);
        }}
      />
    </FormControl>
  );
};

export default PackageSelectView;
